import React, { createContext, useState, useContext, ReactNode } from 'react';
import { Toaster } from 'src/components/Toaster';
import { ActionConfig } from 'src/components/ModalV2';

interface IToastContext {
  toast: {
    open: boolean;
    message: string | ReactNode;
    heading: string;
    type: 'error' | 'warning' | 'info' | 'success' | 'neutral';
    submitActionConfig?: ActionConfig;
    closeActionConfig?: ActionConfig;
  };
  success: (
    heading: string,
    message: string | ReactNode,
    submitActionConfig?: ActionConfig,
    closeActionConfig?: ActionConfig,
  ) => void;
  error: (
    heading: string,
    message: string | ReactNode,
    submitActionConfig?: ActionConfig,
    closeActionConfig?: ActionConfig,
  ) => void;
  info: (
    heading: string,
    message: string | ReactNode,
    submitActionConfig?: ActionConfig,
    closeActionConfig?: ActionConfig,
  ) => void;
  warning: (
    heading: string,
    message: string | ReactNode,
    submitActionConfig?: ActionConfig,
    closeActionConfig?: ActionConfig,
  ) => void;
  neutral: (
    heading: string,
    message: string | ReactNode,
    submitActionConfig?: ActionConfig,
    closeActionConfig?: ActionConfig,
    isBlocking?: boolean,
  ) => void;
  confirm: (
    heading: string,
    message: string | ReactNode,
    options?: {
      confirmLabel?: string;
      cancelLabel?: string;
      type?: 'warning' | 'info' | 'neutral';
    },
  ) => Promise<boolean>;
  clearToast: () => void;
}

export const ToastContext = createContext<IToastContext | undefined>(undefined);

interface Props {
  children?: React.ReactNode;
}
type toastType = {
  open: boolean;
  message: string | ReactNode;
  heading: string;
  type: 'error' | 'warning' | 'info' | 'success' | 'neutral';
  submitActionConfig?: ActionConfig;
  closeActionConfig?: ActionConfig;
  isBlocking?: boolean;
};

export const ToastProvider: React.FC<Props> = ({ children }) => {
  const [toast, setToast] = useState<toastType>({
    open: false,
    message: '',
    heading: '',
    type: 'success' as 'error' | 'warning' | 'info' | 'success',
    submitActionConfig: undefined,
    closeActionConfig: undefined,
    isBlocking: false,
  });

  // Store the promise resolver for confirm dialogs
  const confirmResolverRef = React.useRef<((value: boolean) => void) | null>(null);

  const success = (
    heading: string,
    message: string | ReactNode,
    submitActionConfig?: ActionConfig,
    closeActionConfig?: ActionConfig,
  ) => {
    setToast({
      open: true,
      message,
      heading,
      type: 'success',
      submitActionConfig,
      closeActionConfig,
    });
  };

  const error = (
    heading: string,
    message: string | ReactNode,
    submitActionConfig?: ActionConfig,
    closeActionConfig?: ActionConfig,
  ) => {
    setToast({
      open: true,
      message,
      heading,
      type: 'error',
      submitActionConfig,
      closeActionConfig,
    });
  };

  const info = (
    heading: string,
    message: string | ReactNode,
    submitActionConfig?: ActionConfig,
    closeActionConfig?: ActionConfig,
  ) => {
    setToast({
      open: true,
      message,
      heading,
      type: 'info',
      submitActionConfig,
      closeActionConfig,
    });
  };

  const warning = (
    heading: string,
    message: string | ReactNode,
    submitActionConfig?: ActionConfig,
    closeActionConfig?: ActionConfig,
  ) => {
    setToast({
      open: true,
      message,
      heading,
      type: 'warning',
      submitActionConfig,
      closeActionConfig,
    });
  };

  const neutral = (
    heading: string,
    message: string | ReactNode,
    submitActionConfig?: ActionConfig,
    closeActionConfig?: ActionConfig,
    isBlocking?: boolean,
  ) => {
    setToast({
      open: true,
      message,
      heading,
      type: 'neutral',
      submitActionConfig,
      closeActionConfig,
      isBlocking,
    });
  };

  /**
   * Shows a blocking confirmation dialog and returns a Promise
   * that resolves to true when user confirms, false when they cancel
   */
  const confirm = (
    heading: string,
    message: string | ReactNode,
    options?: {
      confirmLabel?: string;
      cancelLabel?: string;
      type: 'neutral';
    },
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      // Store the resolver so we can call it from button handlers
      confirmResolverRef.current = resolve;

      const handleConfirm = () => {
        setToast((prevState) => ({ ...prevState, open: false }));
        if (confirmResolverRef.current) {
          confirmResolverRef.current(true);
          confirmResolverRef.current = null;
        }
      };

      const handleCancel = () => {
        setToast((prevState) => ({ ...prevState, open: false }));
        if (confirmResolverRef.current) {
          confirmResolverRef.current(false);
          confirmResolverRef.current = null;
        }
      };

      setToast({
        open: true,
        message,
        heading,
        type: options?.type || 'neutral',
        isBlocking: true, // Always blocking for confirm dialogs
        submitActionConfig: {
          id: 'toast-confirm-btn',
          label: options?.confirmLabel || 'Confirm',
          handleAction: handleConfirm,
        },
        closeActionConfig: {
          id: 'toast-cancel-btn',
          label: options?.cancelLabel || 'Cancel',
          handleAction: handleCancel,
        },
      });
    });
  };

  const clearToast = () => {
    setToast((prevState) => ({ ...prevState, open: false }));
    // If there's a pending confirmation, resolve it as false (cancelled)
    if (confirmResolverRef.current) {
      confirmResolverRef.current(false);
      confirmResolverRef.current = null;
    }
  };

  return (
    <ToastContext.Provider
      value={{ toast, success, error, info, warning, neutral, confirm, clearToast }}
    >
      {children}
      <Toaster
        open={toast.open}
        message={toast.message}
        heading={toast.heading}
        type={toast.type}
        submitActionConfig={toast.submitActionConfig}
        closeActionConfig={toast.closeActionConfig}
        setOpen={clearToast}
        isBlocking={toast.isBlocking}
      />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
